/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Module for header on Expense Inventory Detail Page
 * @module src/components/Inventory/expense/detail/ExpenseInventoryDetailHeader
 */
import React, { FC, ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import Dropdown from '@salesforce/design-system-react/components/menu-dropdown';
import PageHeader from '@salesforce/design-system-react/components/page-header';
import PageHeaderControl from '@salesforce/design-system-react/components/page-header/control';
import ButtonGroup from '@salesforce/design-system-react/components/button-group';
import Button from '@salesforce/design-system-react/components/button';
import { format, isValid } from 'date-fns';
import EventStatusBadge from '../../../../Shared/EventStatusBadge';
import DisplayRecordIndicator from '../../../../Shared/DisplayRecordIndicator';
import { ResolvedFieldConfig, isFieldVisible } from '../../form/expenseInventoryConfig';
import {
  EXPENSE_INVENTORY_CASE_STATUS,
  EXPENSE_INVENTORY_ROUTES,
} from '../../../../../util/constants';
import { getEventIcon } from '../../../../../util/utilityFunctions';
import './index.scss';

interface ExpenseInventoryDetail {
  caseNumber?: string;
  status?: string;
  accountId?: string;
  accountDisplay?: string;
  salesRepName?: string;
  shipTo?: string;
  shipToDisplay?: string;
  branch?: string;
  region?: string;
  territory?: string;
  submittedDate?: string;
  orderType?: string;
  failureReason?: string;
  erpOrderNumber?: string;
  doctorName?: string;
  facilityNameOrLocation?: string;
  usageDate?: string;
  usedInPatientCare?: boolean;
  marketingCollaboration?: boolean;
  writeOffType?: string;
  createdDate?: string;
  createdBy?: string;
  updatedDate?: string;
  updatedBy?: string;
}

interface Props {
  detail?: ExpenseInventoryDetail;
  fieldConfig?: ResolvedFieldConfig;
  type?: string;
  opsAction?: string;
  isEdit?: boolean;
  viewOnly?: boolean;
  isReviewMode?: boolean;
  reviewSubmitReady?: boolean;
  onEditPress?: () => void;
  onSubmitPress?: () => void;
  onCancelPress?: () => void;
}

const formatDate = (date?: string): string => {
  if (!date) {
    return '--';
  }
  const parsedDate = new Date(date);
  if (!isValid(parsedDate)) {
    return '--';
  }
  const utcDate = new Date(parsedDate.getTime() + parsedDate.getTimezoneOffset() * 60000);
  return format(utcDate, 'MM/dd/yyyy');
};

const ExpenseInventoryDetailHeader: FC<Props> = ({
  detail,
  fieldConfig = {},
  type,
  opsAction,
  viewOnly,
  isReviewMode = false,
  reviewSubmitReady = false,
  onEditPress,
  onSubmitPress,
  onCancelPress,
}) => {
  const {
    caseNumber,
    status,
    accountId,
    accountDisplay,
    salesRepName,
    shipToDisplay,
    region,
    territory,
    failureReason,
    erpOrderNumber,
    doctorName,
    facilityNameOrLocation,
    usageDate,
    usedInPatientCare,
    marketingCollaboration,
    writeOffType,
  } = detail || {};
  const history = useHistory();

  // Opened from the Review/Assign Requests queue (opsAction set) -- go back to that
  // list rather than the base Expense Inventory list's Open tab.
  const handleNavigateToExpenseList = (): void => {
    if (opsAction) {
      history.goBack();
      return;
    }
    history.push(EXPENSE_INVENTORY_ROUTES.BASE);
  };

  const actions = (): ReactNode => {
    // viewOnly (computed by the parent from persona, case status, and opsAction) is the
    // single source of truth for whether Edit/Submit are allowed. Cancel stays New-only
    // regardless of the Ops Requested exception. In Ops review mode, Submit instead
    // finalizes line decisions (reviewExpenseInventory submit:true) and stays disabled
    // until every line has a decision (reviewSubmitReady, reported by the Sampling tab).
    const isNew = !!detail && detail.status === EXPENSE_INVENTORY_CASE_STATUS.NEW;
    const canEdit = !viewOnly;
    const canSubmit = isReviewMode ? !viewOnly && reviewSubmitReady : !viewOnly;
    const canCancel = !viewOnly && isNew;

    const dropDown = [
      {
        label: 'Edit',
        value: 'edit',
        disabled: !canEdit,
      },
      {
        label: 'Submit',
        value: 'submit',
        disabled: !canSubmit,
      },
      {
        label: 'Cancel',
        value: 'cancel',
        disabled: !canCancel,
      },
    ];

    const handleSelect = (value: string): void => {
      if (value === 'edit' && onEditPress) {
        onEditPress();
      } else if (value === 'submit' && onSubmitPress) {
        onSubmitPress();
      } else if (value === 'cancel' && onCancelPress) {
        onCancelPress();
      }
    };

    return (
      <PageHeaderControl>
        <ButtonGroup
          className="slds-align-middle"
          variant="list"
          id="button-group-page-header-actions"
        >
          <Dropdown
            className="expense-inventory-detail-header__actions-dropdown"
            iconCategory="utility"
            iconSize="small"
            align="right"
            iconName="down"
            iconPosition="right"
            label="Actions"
            options={dropDown}
            onSelect={(selected: { value: string }): void => handleSelect(selected.value)}
          />
          <DisplayRecordIndicator recordInfo={detail} />
        </ButtonGroup>
      </PageHeaderControl>
    );
  };

  const iconPath = getEventIcon('Inventory');

  return (
    <IconSettings iconPath="/icons">
      <div className="slds-x-small-buttons_horizontal expense-inventory-detail-header__back-button-wrapper">
        <Button
          className="expense-inventory-detail-header__back-button"
          iconCategory="utility"
          iconName="back"
          iconPosition="left"
          label="Back"
          onClick={handleNavigateToExpenseList}
        />
      </div>
      <PageHeader
        className="slds-m-around_small slds-m-bottom_none"
        icon={
          <img
            src={iconPath}
            className="logo-image expense-inventory-detail-header__icon"
            alt="expense inventory"
          />
        }
        label="Expense Inventory"
        onRenderActions={actions}
        title={
          <div className="slds-grid slds-grid_vertical-align-center slds-p-top_xx-small">
            <span className="slds-col event_id">{caseNumber || 'TBD'}</span>
            <span className="slds-col slds-m-left_large">
              <div className="expense-inventory-detail-header__status" title="Status">
                <EventStatusBadge status={status || '--'} />
              </div>
            </span>
          </div>
        }
        variant="record-home"
      />
      <div className="wrapper">
        <ul className="slds-page-header__detail-row slds-grid slds-m-around_small slds-m-top_none slds-m-bottom_none expense-inventory-detail-header__detail-row">
          {isFieldVisible(fieldConfig, 'salesRep') && (
            <li className="slds-page-header__detail-block slds-col slds-size_3-of-12">
              <div
                className="slds-text-title expense-inventory-detail-header__field-title"
                title="Sales Rep"
              >
                Sales Rep
              </div>
              <div className="slds-truncate" title={salesRepName || '--'}>
                {salesRepName || '--'}
              </div>
            </li>
          )}
          {isFieldVisible(fieldConfig, 'account') && (
            <li className="slds-page-header__detail-block slds-col slds-size_3-of-12">
              <div
                className="slds-text-title expense-inventory-detail-header__field-title"
                title="Account"
              >
                Account
              </div>
              <div className="slds-truncate" title={accountDisplay || accountId || '--'}>
                {accountDisplay || accountId || '--'}
              </div>
            </li>
          )}
          {isFieldVisible(fieldConfig, 'shipTo') && (
            <li className="slds-page-header__detail-block slds-col slds-size_3-of-12">
              <div
                className="slds-text-title expense-inventory-detail-header__field-title"
                title="Ship To"
              >
                Ship To
              </div>
              <div className="slds-truncate" title={shipToDisplay || '--'}>
                {shipToDisplay || '--'}
              </div>
            </li>
          )}
        </ul>
        <ul className="slds-page-header__detail-row slds-grid slds-m-around_small slds-m-top_none slds-m-bottom_none expense-inventory-detail-header__detail-row">
          {isFieldVisible(fieldConfig, 'writeOffType') && (
            <li className="slds-page-header__detail-block slds-col slds-size_3-of-12">
              <div
                className="slds-text-title expense-inventory-detail-header__field-title"
                title="Write Off Type"
              >
                Write Off Type
              </div>
              <div title={writeOffType || '--'}>{writeOffType || '--'}</div>
            </li>
          )}
          {isFieldVisible(fieldConfig, 'doctorName') && (
            <li className="slds-page-header__detail-block slds-col slds-size_3-of-12">
              <div
                className="slds-text-title expense-inventory-detail-header__field-title"
                title="Doctor Name"
              >
                Doctor Name
              </div>
              <div className="slds-truncate" title={doctorName || '--'}>
                {doctorName || '--'}
              </div>
            </li>
          )}
          {isFieldVisible(fieldConfig, 'facilityNameOrLocation') && (
            <li className="slds-page-header__detail-block slds-col slds-size_3-of-12">
              <div
                className="slds-text-title expense-inventory-detail-header__field-title"
                title="Facility Name or Location"
              >
                Facility Name or Location
              </div>
              <div className="slds-truncate" title={facilityNameOrLocation || '--'}>
                {facilityNameOrLocation || '--'}
              </div>
            </li>
          )}
          {isFieldVisible(fieldConfig, 'usageDate') && (
            <li className="slds-page-header__detail-block slds-col slds-size_3-of-12">
              <div
                className="slds-text-title expense-inventory-detail-header__field-title"
                title="Usage Date"
              >
                Usage Date
              </div>
              <div title={formatDate(usageDate)}>{formatDate(usageDate)}</div>
            </li>
          )}
        </ul>
        <ul className="slds-page-header__detail-row slds-grid slds-m-around_small slds-m-top_none slds-m-bottom_none expense-inventory-detail-header__detail-row">
          {isFieldVisible(fieldConfig, 'usedInPatientCare') && (
            <li className="slds-page-header__detail-block slds-col slds-size_3-of-12">
              <div
                className="slds-text-title expense-inventory-detail-header__field-title"
                title="Used in Patient Care"
              >
                Used in Patient Care
              </div>
              <div title={usedInPatientCare ? 'Yes' : 'No'}>{usedInPatientCare ? 'Yes' : 'No'}</div>
            </li>
          )}
          {isFieldVisible(fieldConfig, 'marketingCollaboration') && (
            <li className="slds-page-header__detail-block slds-col slds-size_3-of-12">
              <div
                className="slds-text-title expense-inventory-detail-header__field-title"
                title="Marketing Collaboration"
              >
                Marketing Collaboration
              </div>
              <div title={marketingCollaboration ? 'Yes' : 'No'}>
                {marketingCollaboration ? 'Yes' : 'No'}
              </div>
            </li>
          )}
          <li className="slds-page-header__detail-block slds-col slds-size_3-of-12">
            <div
              className="slds-text-title expense-inventory-detail-header__field-title"
              title="Region"
            >
              Region
            </div>
            <div className="slds-truncate" title={region || '--'}>
              {region || '--'}
            </div>
          </li>
          <li className="slds-page-header__detail-block slds-col slds-size_3-of-12">
            <div
              className="slds-text-title expense-inventory-detail-header__field-title"
              title="Territory"
            >
              Territory
            </div>
            <div className="slds-truncate" title={territory || '--'}>
              {territory || '--'}
            </div>
          </li>
        </ul>
        <ul className="slds-page-header__detail-row slds-grid slds-m-around_small slds-m-top_none slds-m-bottom_none expense-inventory-detail-header__detail-row">
          {type === 'Processed' && (
            <li className="slds-page-header__detail-block slds-col slds-size_3-of-12">
              <div
                className="slds-text-title expense-inventory-detail-header__field-title"
                title="ERP Order Number"
              >
                ERP Order Number
              </div>
              <div title={erpOrderNumber || '--'}>{erpOrderNumber || '--'}</div>
            </li>
          )}
          {type === 'Failed' && (
            <li className="slds-page-header__detail-block slds-col slds-size_3-of-12">
              <div
                className="slds-text-title expense-inventory-detail-header__field-title"
                title="Failure Reason"
              >
                Failure Reason
              </div>
              <div title={failureReason || '--'}>{failureReason || '--'}</div>
            </li>
          )}
        </ul>
      </div>
    </IconSettings>
  );
};

/** Custom Header on Expense Inventory Detail Page **/
export default ExpenseInventoryDetailHeader;
